import { useEffect, useMemo, useRef, useState } from 'react';
import { crypto as cv, type dto } from '@photo-vault/shared';
import { Button, Card, Icon, type AlbumPickerOption } from '../../ui/index.js';
import { useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { type BulkProgress, type FileProgress } from '../upload/uploadBulk.js';
import { useAlbumUploads, useUploads } from '../upload/UploadContext.js';
import { deleteSession, listAlbumSessions, type PersistedSession } from '../upload/persistence.js';
import { MediaGrid } from '../viewer/MediaGrid.js';
import { MovePhotosDialog } from './MovePhotosDialog.js';
import { PhotoSortControl } from './PhotoSortControl.js';
import {
  loadSortPreference,
  saveSortPreference,
  sortPhotoRows,
  type SortState,
} from './photoSort.js';
import { decryptRating, encryptRating } from '../rating/ratingCrypto.js';
import { TagPicker } from '../tags/TagPicker.js';
import { BulkTagDialog } from '../tags/BulkTagDialog.js';
import { decryptTagsCached, forgetRoot, type DecryptedTag } from '../tags/tagCache.js';

interface Props {
  albumId: string;
  // Root album of the current node; needed for tag attach/detach calls and
  // for fetching the tags list (tags are scoped per-root).
  rootAlbumId: string;
  albumKey: CryptoKey;
  // Pre-decrypted picker options for "move into…" — only intra-root nodes
  // excluding the current album. Caller (AlbumDetailPage) passes them so we
  // don't re-fetch and re-decrypt the tree here.
  movePickerOptions: AlbumPickerOption[];
}

interface PhotoRow {
  photo: dto.Photo;
  filename: string;
  mimeType?: string;
  // null while the downloads/batch chunk for this row hasn't arrived yet —
  // the grid surfaces an `awaiting-url` placeholder for those tiles instead
  // of stalling the whole render until the last chunk resolves.
  url: string | null;
  // Decrypted rating 1..5, or null when the photo has no encryptedRating or
  // its decrypt failed. Optimistically updated by handleRate.
  rating: number | null;
}

const PHASE_LABEL: Record<FileProgress['status'], string> = {
  pending: 'In attesa',
  encrypted: 'Cifrato',
  uploading: 'Carico',
  complete: 'Completato',
  failed: 'Errore',
};

export function AlbumPhotosSection({ albumId, rootAlbumId, albumKey, movePickerOptions }: Props) {
  const session = useAuthSession();
  const { metadata } = useInfra();
  const { startUpload } = useUploads();
  const albumSessions = useAlbumUploads(albumId);
  const [rows, setRows] = useState<PhotoRow[] | 'loading' | { error: string }>('loading');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [pendingSessions, setPendingSessions] = useState<PersistedSession[]>([]);
  const [moveDialogIds, setMoveDialogIds] = useState<string[] | null>(null);
  const [tagPickerPhotoId, setTagPickerPhotoId] = useState<string | null>(null);
  // Photo ids the bulk-tag dialog is currently editing. Open ↔ non-null.
  const [bulkTagIds, setBulkTagIds] = useState<string[] | null>(null);
  // Lazily-loaded list of decrypted root tags for the picker. Loaded on the
  // first time the user opens it; mutated on inline create. Not preloaded
  // because most album visits never touch the picker.
  const [availableTags, setAvailableTags] = useState<DecryptedTag[] | null>(null);
  const [sort, setSort] = useState<SortState>(() => loadSortPreference());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const runningSession = albumSessions.find((s) => s.status === 'running');
  const progress: BulkProgress | null = runningSession?.progress ?? null;
  const uploading = runningSession !== undefined;

  async function reload() {
    setRows('loading');
    try {
      const { photos } = await metadata.listAlbumPhotos(albumId, session.jwt);
      if (photos.length === 0) {
        setRows([]);
        return;
      }
      const decryptedMeta = await Promise.all(
        photos.map(async (p) => {
          const ratingPromise = p.encryptedRating
            ? decryptRating(p.encryptedRating, albumKey)
            : Promise.resolve<number | null>(null);
          try {
            const json = await cv.decryptText(p.encryptedMeta, albumKey);
            const meta = JSON.parse(json) as { filename?: string; mimeType?: string };
            const rating = await ratingPromise;
            return { photo: p, filename: meta.filename ?? p.id, mimeType: meta.mimeType, rating };
          } catch {
            const rating = await ratingPromise;
            return { photo: p, filename: '(meta non leggibile)', rating };
          }
        }),
      );
      // Render the grid immediately with url=null so each tile can show
      // its own "awaiting-url" placeholder while the presigned batches are
      // still in flight. We then stream the chunks and patch the urls in.
      const initialRows: PhotoRow[] = decryptedMeta.map((m) => ({
        photo: m.photo,
        filename: m.filename,
        ...('mimeType' in m && m.mimeType ? { mimeType: m.mimeType } : {}),
        url: null,
        rating: m.rating,
      }));
      setRows(initialRows);

      await metadata.downloadsBatchAllStreaming(
        photos.map((p) => ({ albumId: p.albumId, photoId: p.id })),
        session.jwt,
        (chunk) => {
          const byId = new Map(chunk.map((it) => [it.photoId, it.url]));
          setRows((current) => {
            if (current === 'loading' || 'error' in current) return current;
            return current.map((r) => {
              if (r.url) return r;
              const u = byId.get(r.photo.id);
              return u ? { ...r, url: u } : r;
            });
          });
        },
      );
    } catch (err) {
      setRows({ error: err instanceof Error ? err.message : 'errore' });
    }
  }

  async function refreshPendingSessions() {
    try {
      const sessions = await listAlbumSessions(albumId);
      setPendingSessions(sessions.filter((s) => s.files.some((f) => f.status !== 'complete')));
    } catch {
      setPendingSessions([]);
    }
  }

  useEffect(() => {
    void reload();
    void refreshPendingSessions();
  }, [albumId, albumKey]);

  function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;
    setUploadError(null);
    startUpload({
      files,
      albumId,
      albumKey,
      token: session.jwt,
      onCompleted: () => {
        void Promise.all([reload(), refreshPendingSessions()]);
      },
      onError: (message) => {
        setUploadError(message);
        void refreshPendingSessions();
      },
    });
  }

  async function discardPending(id: string) {
    await deleteSession(id);
    await refreshPendingSessions();
  }

  async function handleDeletePhoto(photoId: string) {
    await metadata.deletePhotosBatch({ items: [{ albumId, photoId }] }, session.jwt);
    setRows((current) => {
      if (current === 'loading' || 'error' in current) return current;
      return current.filter((r) => r.photo.id !== photoId);
    });
  }

  async function handleBulkDelete(ids: string[]) {
    if (!window.confirm(`Eliminare ${ids.length} ${ids.length === 1 ? 'foto' : 'foto'}?`)) return;
    await metadata.deletePhotosBatch(
      { items: ids.map((photoId) => ({ albumId, photoId })) },
      session.jwt,
    );
    setRows((current) => {
      if (current === 'loading' || 'error' in current) return current;
      const removed = new Set(ids);
      return current.filter((r) => !removed.has(r.photo.id));
    });
  }

  function handleBulkMove(ids: string[]) {
    setMoveDialogIds(ids);
  }

  // Bulk-tag entry point: ensure we have the decrypted tag list ready (so the
  // dialog opens already populated) and then open the modal with the ids.
  async function handleBulkTag(ids: string[]) {
    try {
      await ensureAvailableTags();
      setBulkTagIds(ids);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'errore');
    }
  }

  // Lazily fetch + decrypt the root's tags the first time the picker opens.
  // The result lives in state for the rest of the album visit; subsequent
  // opens reuse it. Page reload re-fetches from the server.
  async function ensureAvailableTags(): Promise<DecryptedTag[]> {
    if (availableTags) return availableTags;
    const { tags } = await metadata.listTags(rootAlbumId, session.jwt);
    const decrypted = await decryptTagsCached(tags, rootAlbumId, albumKey);
    setAvailableTags(decrypted);
    return decrypted;
  }

  async function openTagPickerFor(photoId: string) {
    try {
      await ensureAvailableTags();
      setTagPickerPhotoId(photoId);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'errore');
    }
  }

  // Optimistic rating update. Any conflict (server's keyGen has moved past
  // ours, e.g. a rotation just landed) triggers a full reload so we re-read
  // fresh keyGens and try again on the next click. Failure paths roll back.
  async function handleRate(photoId: string, rating: number) {
    if (rows === 'loading' || 'error' in rows) return;
    const row = rows.find((r) => r.photo.id === photoId);
    if (!row) return;
    const prev = row.rating;
    const next = rating === 0 ? null : rating;
    setRows((current) => {
      if (current === 'loading' || 'error' in current) return current;
      return current.map((r) => (r.photo.id === photoId ? { ...r, rating: next } : r));
    });
    try {
      const encryptedRating = next === null ? undefined : await encryptRating(next, albumKey);
      const resp = await metadata.ratePhotosBatch(
        {
          rootAlbumId: row.photo.rootAlbumId,
          items: [
            {
              albumId: row.photo.albumId,
              photoId: row.photo.id,
              keyGen: row.photo.keyGen ?? 1,
              ...(encryptedRating ? { encryptedRating } : {}),
            },
          ],
        },
        session.jwt,
      );
      if (resp.conflicts.includes(photoId)) {
        await reload();
      }
    } catch (err) {
      setRows((current) => {
        if (current === 'loading' || 'error' in current) return current;
        return current.map((r) => (r.photo.id === photoId ? { ...r, rating: prev } : r));
      });
      window.alert(err instanceof Error ? err.message : 'Errore aggiornamento valutazione');
    }
  }

  async function confirmMove(destinationAlbumId: string) {
    if (!moveDialogIds) return;
    await metadata.movePhotosBatch(
      {
        items: moveDialogIds.map((photoId) => ({ albumId, photoId })),
        destinationAlbumId,
      },
      session.jwt,
    );
    const moved = new Set(moveDialogIds);
    setRows((current) => {
      if (current === 'loading' || 'error' in current) return current;
      return current.filter((r) => !moved.has(r.photo.id));
    });
    setMoveDialogIds(null);
  }

  const summary = progress
    ? `${progress.uploaded} / ${progress.total} foto · cifrate ${progress.encrypted}`
    : null;

  const sortedRows = useMemo(
    () => (Array.isArray(rows) ? sortPhotoRows(rows, sort) : null),
    [rows, sort],
  );

  function handleSortChange(next: SortState) {
    setSort(next);
    saveSortPreference(next);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-display text-lg font-semibold">Media</h3>
        <div className="flex items-center gap-2">
          {Array.isArray(rows) && rows.length > 1 ? (
            <PhotoSortControl value={sort} onChange={handleSortChange} />
          ) : null}
          <Button
            leftIcon={<Icon name="upload" />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            Carica
          </Button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={handleFiles}
        />
      </div>

      {pendingSessions.length > 0 && !uploading ? (
        <Card className="flex flex-col gap-2 border border-warning/30 bg-warning/5">
          <p className="text-sm">
            <span className="font-semibold">Upload incompleti.</span>{' '}
            {pendingSessions.length === 1
              ? "C'è una sessione di upload in sospeso per questo album."
              : `Ci sono ${pendingSessions.length} sessioni di upload in sospeso.`}{' '}
            Riselleziona i file per riprendere o scarta lo stato salvato.
          </p>
          <div className="flex flex-wrap gap-2 text-xs">
            {pendingSessions.map((s) => {
              const done = s.files.filter((f) => f.status === 'complete').length;
              return (
                <button
                  key={s.id}
                  onClick={() => void discardPending(s.id)}
                  className="rounded-full border border-warning/40 px-3 py-1 hover:bg-warning/10"
                  title="Scarta lo stato salvato"
                >
                  {done}/{s.files.length} · scarta
                </button>
              );
            })}
          </div>
        </Card>
      ) : null}

      {progress ? (
        <Card className="flex flex-col gap-3">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold">Upload in corso</span>
            <span className="text-on-surface-variant">{summary}</span>
          </div>
          <BulkProgressBar progress={progress} />
          <FileList files={progress.files} />
        </Card>
      ) : null}

      {uploadError ? <p className="text-sm text-error">{uploadError}</p> : null}

      {rows === 'loading' ? (
        <p className="text-sm text-on-surface-variant">Carico foto…</p>
      ) : 'error' in rows ? (
        <p className="text-sm text-error">Errore: {rows.error}</p>
      ) : rows.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 py-12 text-center">
          <Icon name="photo_library" className="text-4xl text-on-surface-variant" />
          <p className="text-sm text-on-surface-variant">
            Nessun media in questo album. Premi "Carica" per aggiungerne.
          </p>
        </Card>
      ) : (
        <MediaGrid
          items={(sortedRows ?? rows).map((r) => ({
            id: r.photo.id,
            url: r.url,
            filename: r.filename,
            ...(r.mimeType ? { mimeType: r.mimeType } : {}),
            rating: r.rating,
            info: {
              encryptedMeta: r.photo.encryptedMeta,
              uploadedAt: r.photo.createdAt,
              encryptedSize: r.photo.size,
              ...(r.photo.keyGen !== undefined ? { keyGen: r.photo.keyGen } : {}),
            },
          }))}
          albumKey={albumKey}
          onDelete={handleDeletePhoto}
          onRate={handleRate}
          onBulkDelete={handleBulkDelete}
          {...(movePickerOptions.length > 0 ? { onBulkMove: handleBulkMove } : {})}
          onBulkTag={handleBulkTag}
          onEditTags={(id) => void openTagPickerFor(id)}
        />
      )}

      <MovePhotosDialog
        open={moveDialogIds !== null}
        count={moveDialogIds?.length ?? 0}
        options={movePickerOptions}
        onClose={() => setMoveDialogIds(null)}
        onConfirm={confirmMove}
        hint="Solo album dello stesso albero radice."
      />

      <TagPicker
        open={tagPickerPhotoId !== null && availableTags !== null}
        photoId={tagPickerPhotoId ?? ''}
        albumId={albumId}
        rootAlbumId={rootAlbumId}
        availableTags={availableTags ?? []}
        albumKey={albumKey}
        onClose={() => setTagPickerPhotoId(null)}
        onTagCreated={(t) => {
          setAvailableTags((prev) => (prev ? [...prev, t] : [t]));
          // The parent owner-only TagsListPage may be open in another tab;
          // dropping the cache nudges the next listTags call to re-decrypt
          // (cheap) so creates remain visible across pages.
          forgetRoot(rootAlbumId);
        }}
      />

      <BulkTagDialog
        open={bulkTagIds !== null && availableTags !== null}
        albumId={albumId}
        rootAlbumId={rootAlbumId}
        photoIds={bulkTagIds ?? []}
        availableTags={availableTags ?? []}
        albumKey={albumKey}
        onClose={() => setBulkTagIds(null)}
        onTagCreated={(t) => {
          setAvailableTags((prev) => (prev ? [...prev, t] : [t]));
          forgetRoot(rootAlbumId);
        }}
      />
    </div>
  );
}

function BulkProgressBar({ progress }: { progress: BulkProgress }) {
  const totalBytes = progress.files.reduce((acc, f) => acc + f.totalBytes, 0);
  const uploadedBytes = progress.files.reduce((acc, f) => acc + f.uploadedBytes, 0);
  const percent = totalBytes > 0 ? Math.min(100, (uploadedBytes / totalBytes) * 100) : 0;
  return (
    <div className="h-2 overflow-hidden rounded-full bg-surface-container-low">
      <div className="h-full bg-cyan transition-all" style={{ width: `${Math.round(percent)}%` }} />
    </div>
  );
}

function FileList({ files }: { files: FileProgress[] }) {
  return (
    <ul className="flex max-h-64 flex-col gap-1 overflow-auto text-xs">
      {files.map((f) => {
        const percent = f.totalBytes > 0 ? Math.round((f.uploadedBytes / f.totalBytes) * 100) : 0;
        return (
          <li
            key={f.clientFileId}
            className="flex items-center justify-between gap-2 rounded px-2 py-1 odd:bg-surface-container-low/40"
          >
            <span className="truncate" title={f.filename}>
              {f.filename}
            </span>
            <span className="shrink-0 text-on-surface-variant">
              {PHASE_LABEL[f.status]}
              {f.status === 'uploading' ? ` ${percent}%` : ''}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
